import { Cesium } from 'app/cesium';
import { TimeZone } from 'app/shared/time/TimeZone';
import { CachedTimestampFormatter, Format } from 'app/shared/utilities/TimestampFormatter';
import moment, { duration, isMoment, Moment } from 'moment-timezone';

export const SECONDS_IN_A_DAY = 60 * 60 * 24;

/**
 * Converts 120 seconds to 00:02:00
 * @param seconds
 * @returns {string}
 */
function formatSeconds(seconds: number): string {
    return new Date(seconds * 1000).toISOString().substr(11, 8);
}

/**
 * Converts a JulianDate to milliseconds elapsed since 1 January 1970 00:00:00 UTC
 * @param {JulianDate} julianDate
 * @returns {number}
 */
export function toMilliseconds(julianDate: Cesium.JulianDate): number {
    return Cesium.JulianDate.toDate(julianDate).getTime();
}

export function getDurationInMinutes(from: Moment, to: Moment): number | undefined {
    return isMoment(from) && isMoment(to) ? duration(to.diff(from)).asMinutes() : undefined;
}

export function unixToDate(unix: number): Date {
    return new Date(unix * 1000);
}

export function unixToJulian(unix: number): Cesium.JulianDate {
    return Cesium.JulianDate.fromDate(unixToDate(unix));
}

export function unixNow(): number {
    return Date.now() / 1000;
}

export function toUnix(date: Date): number {
    if (date === null) return null;
    return date.getTime() / 1000;
}

export function fromUnix(unix: number): Date {
    return unix == null ? null : new Date(unix * 1000);
}

export function julianToUnix(julian: Cesium.JulianDate): number {
    // A more efficient implementation compared to Cesium julian > js date > unix
    const secondsAheadOfUTC = Cesium.JulianDate.computeTaiMinusUtc(julian);
    // From wikipedia: (JD − 2440587.5) × 86400
    // Still need to adjust for seconds in day and utc offset
    return (julian.dayNumber - 2440587.5) * 86400 + julian.secondsOfDay - secondsAheadOfUTC;
}

// yes this is an ugly global because caching is important for performance,
// but so are static imports for sanity.
const cachedFormatter = new CachedTimestampFormatter();

export function formatMoment(
    moment: Moment,
    timeZone: string,
    format: Format = Format.LONG_DATE_TIME,
): string {
    return cachedFormatter.formatMoment(moment, timeZone, format);
}

// e.g. formatDate(utcTimestamp, TimeZone.UTC.string, Format.ISO_8601); to get ISO8601
export function formatDate(
    date: Date,
    timeZone: string,
    format: Format = Format.LONG_DATE_TIME,
): string {
    return cachedFormatter.formatDate(date, timeZone, format);
}

export function formatUnix(
    unix: number,
    timeZone: string,
    format: Format = Format.LONG_DATE_TIME,
): string {
    return cachedFormatter.formatUnix(unix, timeZone, format);
}

/**
 * Checks whether an object is a valid date object
 * >>> isValid(new Date(1))         === true
 * >>> isValid(new Date('poop'))    === false
 * >>> isValid(null)                === false
 * @param date The date instance to check
 */
export function isValid(date: Date): date is Date {
    return date instanceof Date && !isNaN(+date);
}

/**
 * Take a date object, strip off the time zone, set a new time zone, and return it as a moment
 *  Eg. take a date representing                2018-05-01T00:00:00+08:00
 *  and turn it into a moment representing      2018-05-01T00:00:00-05:00
 *
 * We have taken a 'naive' date object, and set the time zone for it.
 * This is useful if you've got a date picker which doesn't respect time zone.
 * Say the user has picked November 7th at 9am. They would have assumed the time zone this is for.
 * Use this function to force the time zone while preserving the hour-minute-second they've picked.
 *
 * @param date
 * @param timeZone
 */
export function transformNaiveDateIntoMoment(date: Date, timeZone: TimeZone): Moment {
    return moment.tz(
        {
            year: date.getFullYear(),
            month: date.getMonth(),
            date: date.getDate(),
            hour: date.getHours(),
            minute: date.getMinutes(),
            second: date.getSeconds(),
            millisecond: date.getMilliseconds(),
        },
        timeZone.string,
    );
}

/**
 * Creates a new date in local time from the given moment.
 * The time zone of the moment will be completely ignored.
 * As in, even if the moment is 5am +07:00, you'll get back a
 * date object which is 5am +08:00 (if +8 is the browser offset).
 *
 * Take a moment object, strip off the old time zone, set a new one, and return as a date.
 *  Eg. take a moment representing          2018-05-01T00:00:00+08:00
 *  and turn it into a date representing    2018-05-01T00:00:00-05:00
 *
 * Note that the current time zone set on the input moment will effect the result.
 * @param m
 */
export function transformNaiveMomentIntoNaiveDate(m: Moment): Date {
    return new Date(
        m.year(),
        m.month(),
        m.date(),
        m.hour(),
        m.minute(),
        m.second(),
        m.millisecond(),
    );
}

export enum TimeUnit {
    SECONDS,
    MINUTES,
    HOURS,
    DAYS,
}

/**
 * Real real simple function, coverts 12 hours into seconds, or 23 days into seconds.
 * Probably most useful in avoiding mental maths.
 */
export function toSeconds(value: number, unit: TimeUnit): number {
    if (unit === TimeUnit.SECONDS) {
        return value;
    } else if (unit === TimeUnit.MINUTES) {
        return value * 60;
    } else if (unit === TimeUnit.HOURS) {
        return value * 60 * 60;
    } else if (unit === TimeUnit.DAYS) {
        return value * 60 * 60 * 24;
    }

    throw new Error(`unit ${unit} not recognized!`);
}

/**
 Convert a duration in seconds to at least hh:mm:ss and at most d day(s), hh:mm:ss
 */
export function formatSecondsAndOverflowToDays(seconds: number): string {
    let remainderSeconds = seconds;
    let days = 0;

    if (seconds >= SECONDS_IN_A_DAY) {
        days = Math.round(seconds / SECONDS_IN_A_DAY);
        remainderSeconds = seconds - days * SECONDS_IN_A_DAY;
    }

    const dayPlural = days == 1 ? 'day' : 'days';
    const dayFriendly = days > 0 ? `${days} ${dayPlural}, ` : '';

    return `${dayFriendly}${formatSeconds(remainderSeconds)}`;
}
